import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  Subject,
  takeUntil
} from 'rxjs';
import {
  AnnouncementInfo,
  AnnouncementType,
  AsteriskVoicemail,
  Voicemessage,
} from '../../shared/services/db/asteriks-entities.interface';
import { SocketIoService } from '../../shared/services/socket-io/socket-io.service';
import {
  VMAILBOX_SOCKET_CONSTANTS,
  VoiceMailboxMessageStateDeleteEvent,
  VoiceMailboxStateDeleteEvent,
  VoiceMailboxStateUpdateEvent,
  VoiceMailboxStateUpdateItem,
} from '../utils/vmailbox-socket.constants';
import { v4 as uuidv4 } from 'uuid';
import { PluginCommunicationService, PluginRegistry } from 'core-client/api';

export interface SetPropertyUpdate {
  id: number;
  isRead: boolean;
}

export interface SetPropertiesResult {
  [messageId: number]: { isRead: boolean };
}

export interface DeleteMessagesResult {
  success: boolean;
  deletedIds: number[];
}

export interface UploadAnnouncementResult {
  success: boolean;
  announcementId: number;
}

/**
 * Per-widget state held for the lifetime of each active widget instance.
 */
interface WidgetState {
  destroy$: Subject<void>;
  mailboxNr$: BehaviorSubject<string | undefined>;
  messageCount$: Subject<VoiceMailboxStateUpdateItem>;
  mailboxDeleted$: Subject<number>;
  messageDeleted$: Subject<{ messageId: number; mailboxId: number }>;
  mailboxNameChanged$: Subject<string>;
  subscribedMailboxId: number | null;
  subscribedMailboxNr: string | null;
  lastKnownItem: VoiceMailboxStateUpdateItem | null;
  lastKnownName: string | null;
  audioBlobCache: Map<string, string>;
}

@Injectable({ providedIn: 'root' })
export class VMailboxWidgetService {
  private readonly http = inject(HttpClient);
  private readonly socketService = inject(SocketIoService);
  private readonly pluginCommunicationService: PluginCommunicationService = inject(PluginCommunicationService);
  private readonly base = '/api/astimax/vmailbox';

  private readonly widgetStateMap: Map<string, WidgetState> = new Map<string, WidgetState>();
  private pluginEventSender: ReturnType<PluginCommunicationService['createSender']> | undefined;

  initializeWidget(widgetId: string, dashboardId: string): void {
    console.log(`[VMailboxWidgetService] Initializing widget: ${widgetId}`);

    const existingMailboxNr$ = this.widgetStateMap.get(widgetId)?.mailboxNr$;

    if (this.widgetStateMap.has(widgetId)) {
      console.warn(`[VMailboxWidgetService] Widget ${widgetId} already initialized — cleaning up first`);
      this.cleanupWidget(widgetId);
    }

    const state: WidgetState = {
      destroy$: new Subject<void>(),
      mailboxNr$: existingMailboxNr$ ?? new BehaviorSubject<string | undefined>(undefined),
      messageCount$: new Subject<VoiceMailboxStateUpdateItem>(),
      mailboxDeleted$: new Subject<number>(),
      messageDeleted$: new Subject<{ messageId: number; mailboxId: number }>(),
      mailboxNameChanged$: new Subject<string>(),
      subscribedMailboxId: null,
      subscribedMailboxNr: null,
      lastKnownItem: null,
      lastKnownName: null,
      audioBlobCache: new Map<string, string>(),
    };

    this.widgetStateMap.set(widgetId, state);
    this.socketService.registerWidget(widgetId, dashboardId);
    this.socketService.registerUnregisterCallback(widgetId, () => {
      console.log(`[VMailboxWidgetService] Socket service unregistered widget ${widgetId} — cleaning local state`);
      this.cleanupLocalState(widgetId);
    });

    // create a plugin event sender with pre-bound 'from' set to 'astimax' (name of the plugin)
    this.pluginEventSender = this.pluginCommunicationService.createSender('astimax');

    this.setupMailboxCountEventListeners(widgetId, state);
  }

  /**
   * Initiate a call for a specific widget
   */
  initiateCall(widgetId: string, phoneNumber: string): void {
    console.log(`[VMailboxWidgetService] Initiating call from widget ${widgetId} to ${phoneNumber}`);

    // send "START_CALL" action to phone module of Astimax plugin
    if (!this.pluginEventSender) {
      console.log(`[VMailboxWidgetService] Plugin event sender not initialized, skipping call`);
      return;
    }

    this.pluginEventSender(PluginRegistry.Astimax.Actions.PhoneModule.START_CALL, {
      targetNumber: phoneNumber,
      withVideo: false
    });
  }

  private cleanupLocalState(widgetId: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (state) {
      this.unsubscribeFromMailboxCounts(widgetId);

      state.destroy$.next();
      state.destroy$.complete();

      state.messageCount$.complete();
      state.mailboxDeleted$.complete();
      state.messageDeleted$.complete();
      state.mailboxNameChanged$.complete();

      state.audioBlobCache.forEach((url: string) => URL.revokeObjectURL(url));
      state.audioBlobCache.clear();

      this.widgetStateMap.delete(widgetId);
    }
  }

  cleanupWidget(widgetId: string): void {
    console.log(`[VMailboxWidgetService] Cleaning up widget: ${widgetId}`);
    this.cleanupLocalState(widgetId);
    this.socketService.unregisterWidget(widgetId);
  }

  destroyWidget(widgetId: string): void {
    const mailboxNr$ = this.widgetStateMap.get(widgetId)?.mailboxNr$;
    this.cleanupWidget(widgetId);
    mailboxNr$?.complete();
  }

  getConnectionState(): Observable<'disconnected' | 'connecting' | 'connected'> {
    return this.socketService.getConnectionState();
  }

  setMailboxNr(widgetId: string, mailboxNr: string | undefined): void {
    this.getOrCreateState(widgetId).mailboxNr$.next(mailboxNr);
  }

  getMailboxNr$(widgetId: string): Observable<string | undefined> {
    return this.getOrCreateState(widgetId).mailboxNr$.asObservable();
  }

  // ─────────────────────────────────────────────────────────────
  // Mailboxes
  // ─────────────────────────────────────────────────────────────

  getMailboxes(): Observable<AsteriskVoicemail[]> {
    return this.http.get<AsteriskVoicemail[]>(this.base);
  }

  getMailbox(mailboxNr: string): Observable<AsteriskVoicemail> {
    return this.http.get<AsteriskVoicemail>(`${this.base}/${mailboxNr}`);
  }

  // ─────────────────────────────────────────────────────────────
  // Messages
  // ─────────────────────────────────────────────────────────────

  getVoicemessages(mailboxNr: string): Observable<Voicemessage[]> {
    return this.http.get<Voicemessage[]>(`${this.base}/${mailboxNr}/messages`);
  }

  setVoicemessagesProperties(updates: SetPropertyUpdate[]): Observable<SetPropertiesResult> {
    return this.http.patch<SetPropertiesResult>(`${this.base}/messages/properties`, { updates });
  }

  deleteVoicemessages(ids: number[]): Observable<DeleteMessagesResult> {
    return this.http.delete<DeleteMessagesResult>(`${this.base}/messages`, { body: { ids } });
  }

  // ─────────────────────────────────────────────────────────────
  // Audio — messages
  // ─────────────────────────────────────────────────────────────

  getMessageAudioUrl(widgetId: string, messageId: number): Observable<string> {
    const state = this.widgetStateMap.get(widgetId);
    const key = `msg-${messageId}`;
    const cached = state?.audioBlobCache.get(key);
    if (cached) return of(cached);
    return this.http
      .get(`${this.base}/messages/${messageId}/stream`, { responseType: 'blob' })
      .pipe(map((blob) => {
        const url = URL.createObjectURL(blob);
        state?.audioBlobCache.set(key, url);
        return url;
      }));
  }

  downloadMessage(messageId: number): Observable<void> {
    return this.http
      .get(`${this.base}/messages/${messageId}/download`, { responseType: 'blob' })
      .pipe(map((blob) => { this.triggerBrowserDownload(blob, `voicemail-${messageId}.wav`); }));
  }

  // ─────────────────────────────────────────────────────────────
  // Announcements
  // ─────────────────────────────────────────────────────────────

  getAnnouncementInfo(mailboxNr: string, type: AnnouncementType): Observable<AnnouncementInfo> {
    return this.http.get<AnnouncementInfo>(`${this.base}/${mailboxNr}/announcements/${type}`);
  }

  getAnnouncementAudioUrl(widgetId: string, mailboxNr: string, type: AnnouncementType): Observable<string> {
    const state = this.widgetStateMap.get(widgetId);
    const key = `ann-${mailboxNr}-${type}`;
    const cached = state?.audioBlobCache.get(key);
    if (cached) return of(cached);
    return this.http
      .get(`${this.base}/${mailboxNr}/announcements/${type}/stream`, { responseType: 'blob' })
      .pipe(map((blob) => {
        const url = URL.createObjectURL(blob);
        state?.audioBlobCache.set(key, url);
        return url;
      }));
  }

  uploadAnnouncement(widgetId: string, mailboxNr: string, type: AnnouncementType, file: File): Observable<UploadAnnouncementResult> {
    const formData = new FormData();
    formData.append('file', file);
    this.invalidateAnnouncementCache(widgetId, mailboxNr, type);
    return this.http.post<UploadAnnouncementResult>(`${this.base}/${mailboxNr}/announcements/${type}`, formData);
  }

  downloadAnnouncement(mailboxNr: string, type: AnnouncementType): Observable<void> {
    return this.http
      .get(`${this.base}/${mailboxNr}/announcements/${type}/download`, { responseType: 'blob' })
      .pipe(map((blob) => { this.triggerBrowserDownload(blob, `announcement-${mailboxNr}-${type}.wav`); }));
  }

  deleteAnnouncement(widgetId: string, mailboxNr: string, type: AnnouncementType): Observable<void> {
    this.invalidateAnnouncementCache(widgetId, mailboxNr, type);
    return this.http.delete<void>(`${this.base}/${mailboxNr}/announcements/${type}`);
  }

  // ─────────────────────────────────────────────────────────────
  // Formatting helpers
  // ─────────────────────────────────────────────────────────────

  formatTimestamp(origtime: string | undefined): string {
    if (!origtime) return '';
    try {
      const date = new Date(Number.parseInt(origtime, 10) * 1000);
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }).format(date);
    } catch {
      return origtime;
    }
  }

  formatDuration(duration: string | undefined): string {
    if (!duration) return '';
    const s = Number.parseInt(duration, 10);
    if (Number.isNaN(s)) return duration;
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  /**
   * Parses an Asterisk callerid string in the format "Name" <Number>.
   *
   * Examples:
   *   "Cappu Cino" <405>  → { name: 'Cappu Cino', number: '405' }
   */
  parseCallerId(callerid: string | undefined): { name: string | undefined; number: string | undefined } {
    if (!callerid?.trim()) return { name: undefined, number: undefined };

    const nameMatch = callerid.match(/^"([^"]*)"/);
    const numberMatch = callerid.match(/<([^>]+)>/);

    const name = nameMatch?.[1]?.trim() || undefined;
    const number = numberMatch?.[1]?.trim() || (!nameMatch ? callerid.trim() : undefined);

    return { name, number };
  }

  // ─────────────────────────────────────────────────────────────
  // Socket — voiceMailboxState subscription
  // ─────────────────────────────────────────────────────────────

  subscribeToMailboxCounts(mailboxId: number, mailboxNr: string, widgetId: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (!state) {
      console.error(`[VMailboxWidgetService] subscribeToMailboxCounts — widget ${widgetId} not initialized`);
      return;
    }

    if (state.subscribedMailboxId === mailboxId && state.subscribedMailboxNr === mailboxNr) {
      console.log(`[VMailboxWidgetService] Already subscribed to mailboxId=${mailboxId} nr=${mailboxNr} for widget ${widgetId}, skipping`);
      return;
    }

    if (state.subscribedMailboxId !== null) {
      console.log(`[VMailboxWidgetService] Switching subscription: mailboxId=${state.subscribedMailboxId} → ${mailboxId} for widget ${widgetId}`);
      this.unsubscribeFromMailboxCounts(widgetId);
    } else {
      console.log(`[VMailboxWidgetService] Subscribing to mailboxId=${mailboxId} nr=${mailboxNr} for widget ${widgetId}`);
    }

    // Count other widgets already subscribed to this mailbox BEFORE updating this widget's state.
    // Only send the server subscribe message if this is the first widget for this mailbox.
    const existingSubscriberCount = this.countMailboxSubscribers(mailboxId);

    state.subscribedMailboxId = mailboxId;
    state.subscribedMailboxNr = mailboxNr;

    if (existingSubscriberCount === 0) {
      const requestId = uuidv4();
      const subscribePayload = {
        id: requestId,
        type: VMAILBOX_SOCKET_CONSTANTS.TYPE,
        service: VMAILBOX_SOCKET_CONSTANTS.SERVICE,
        method: VMAILBOX_SOCKET_CONSTANTS.METHOD_SUBSCRIBE,
        payload: {
          eventType: VMAILBOX_SOCKET_CONSTANTS.EVENT_TYPE,
          metadata: { mailboxIds: [String(mailboxId)] },
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: VMAILBOX_SOCKET_CONSTANTS.VERSION,
          traceId: requestId,
          correlationId: requestId,
          noResponse: true,
        },
      };
      console.log(`[VMailboxWidgetService] Socket subscribe payload:`, subscribePayload);
      this.socketService.emitEvent(VMAILBOX_SOCKET_CONSTANTS.EMIT_REQUEST, subscribePayload, widgetId);
      console.log(`[VMailboxWidgetService] Socket subscribe sent — nr=${mailboxNr} widget=${widgetId}`);
    } else {
      console.log(`[VMailboxWidgetService] Server already subscribed to mailboxId=${mailboxId} nr=${mailboxNr} (${existingSubscriberCount} other subscriber(s)), skipping server subscribe for widget ${widgetId}`);
    }
  }

  unsubscribeFromMailboxCounts(widgetId: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (!state || state.subscribedMailboxId === null || state.subscribedMailboxNr === null) {
      console.log(`[VMailboxWidgetService] unsubscribeFromMailboxCounts — nothing to unsubscribe for widget ${widgetId}`);
      return;
    }

    const mailboxNr = state.subscribedMailboxNr;
    const mailboxId = state.subscribedMailboxId;
    console.log(`[VMailboxWidgetService] Unsubscribing from mailbox mailboxId=${mailboxId} nr=${mailboxNr} for widget ${widgetId}`);

    // Clear this widget's state first, then count remaining subscribers.
    // Only send the server unsubscribe when no other widget is still subscribed to this mailbox.
    state.subscribedMailboxId = null;
    state.subscribedMailboxNr = null;
    state.lastKnownItem = null;

    const remainingSubscriberCount = this.countMailboxSubscribers(mailboxId);
    if (remainingSubscriberCount === 0) {
      console.log(`[VMailboxWidgetService] Last subscriber removed for nr=${mailboxNr} — sending server unsubscribe`);
      const requestId = uuidv4();
      const unsubPayload = {
        id: requestId,
        type: VMAILBOX_SOCKET_CONSTANTS.TYPE,
        service: VMAILBOX_SOCKET_CONSTANTS.SERVICE,
        method: VMAILBOX_SOCKET_CONSTANTS.METHOD_UNSUBSCRIBE,
        payload: {
          eventType: VMAILBOX_SOCKET_CONSTANTS.EVENT_TYPE,
          metadata: { mailboxIds: [String(mailboxId)] },
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: VMAILBOX_SOCKET_CONSTANTS.VERSION,
          traceId: requestId,
          correlationId: requestId,
          noResponse: true,
        },
      };
      console.log(`[VMailboxWidgetService] Socket unsubscribe payload:`, unsubPayload);
      this.socketService.emitEvent(VMAILBOX_SOCKET_CONSTANTS.EMIT_REQUEST, unsubPayload, widgetId);
    } else {
      console.log(`[VMailboxWidgetService] mailboxId=${mailboxId} still has ${remainingSubscriberCount} subscriber(s), skipping server unsubscribe for widget ${widgetId}`);
    }
  }

  onMessageCountUpdate(widgetId: string): Observable<VoiceMailboxStateUpdateItem> {
    return this.getOrCreateState(widgetId).messageCount$.asObservable();
  }

  onMailboxDeleted(widgetId: string): Observable<number> {
    return this.getOrCreateState(widgetId).mailboxDeleted$.asObservable();
  }

  onMessageDeleted(widgetId: string): Observable<{ messageId: number; mailboxId: number }> {
    return this.getOrCreateState(widgetId).messageDeleted$.asObservable();
  }

  onMailboxNameChanged(widgetId: string): Observable<string> {
    return this.getOrCreateState(widgetId).mailboxNameChanged$.asObservable();
  }

  /**
   * Seeds the lastKnownName from the HTTP-loaded mailbox name so the first
   * socket event can detect a name change that happened between HTTP load
   * and event delivery.
   */
  setLastKnownName(widgetId: string, name: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (state && state.lastKnownName === null) {
      state.lastKnownName = name;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Registers the voiceMailboxState event listener once, at widget initialization.
   */
  private setupMailboxCountEventListeners(widgetId: string, state: WidgetState): void {
    console.log(`[VMailboxWidgetService] Setting up event listeners for widget: ${widgetId}`);

    // On disconnect: all server-side subscriptions are lost for this socket connection.
    // Reset local state so the reconnect guard allows re-subscription when the socket reconnects.
    this.socketService.getConnectionState().pipe(
      takeUntil(state.destroy$),
    ).subscribe((connectionState) => {
      if (connectionState === 'disconnected' && state.subscribedMailboxNr !== null) {
        state.subscribedMailboxId = null;
        state.subscribedMailboxNr = null;
        state.lastKnownItem = null;
      }
    });

    this.socketService
      .onServiceEvent<VoiceMailboxStateUpdateEvent | VoiceMailboxStateDeleteEvent | VoiceMailboxMessageStateDeleteEvent>(
        VMAILBOX_SOCKET_CONSTANTS.LISTEN_ON_EVENT,
        widgetId,
        VMAILBOX_SOCKET_CONSTANTS.SERVICE,
        [VMAILBOX_SOCKET_CONSTANTS.LISTEN_UPDATE, VMAILBOX_SOCKET_CONSTANTS.LISTEN_DELETE, VMAILBOX_SOCKET_CONSTANTS.LISTEN_MESSAGE_DELETE]
      )
      .pipe(takeUntil(state.destroy$))
      .subscribe((event: unknown) => {
        console.log(`[VMailboxWidgetService] Event received for widget ${widgetId}:`, event);
        const e = event as
          | VoiceMailboxStateUpdateEvent
          | VoiceMailboxStateDeleteEvent
          | VoiceMailboxMessageStateDeleteEvent;
        const subscribedId = state.subscribedMailboxId;

        if (e?.method === VMAILBOX_SOCKET_CONSTANTS.LISTEN_UPDATE) {
          const update = e as VoiceMailboxStateUpdateEvent;
          const item =
            subscribedId !== null
              ? update.payload.find(
                  (i: VoiceMailboxStateUpdateItem) => i.id === subscribedId,
                )
              : undefined;
          if (item) {
            const prev = state.lastKnownItem;
            const countsChanged =
              !prev ||
              prev.messages.new !== item.messages.new ||
              prev.messages.oldM !== item.messages.oldM;

            state.lastKnownItem = item;

            if (countsChanged) {
              console.log(
                `[VMailboxWidgetService] Count update for widget ${widgetId} — mailbox=${item.number} new=${item.messages.new} old=${item.messages.oldM}`,
              );
              state.messageCount$.next(item);
            } else {
              console.log(
                `[VMailboxWidgetService] State update (no count change) for widget ${widgetId} — mailbox=${item.number} name=${item.name}, skipping reload`,
              );
            }

            console.log(
              `[VMailboxWidgetService] Name check for widget ${widgetId} — ` +
              `lastKnownName="${state.lastKnownName}" eventName="${item.name}" ` +
              `match=${state.lastKnownName === item.name}`,
            );
            if (state.lastKnownName !== null && state.lastKnownName !== item.name) {
              console.log(
                `[VMailboxWidgetService] Mailbox name changed for widget ${widgetId} — "${state.lastKnownName}" → "${item.name}"`,
              );
              state.mailboxNameChanged$.next(item.name);
            }
            state.lastKnownName = item.name;
          }
        } else if (e?.method === VMAILBOX_SOCKET_CONSTANTS.LISTEN_DELETE) {
          const del = e as VoiceMailboxStateDeleteEvent;
          if (subscribedId !== null && del.payload.id === subscribedId) {
            console.log(
              `[VMailboxWidgetService] Mailbox deleted — mailboxId=${del.payload.id}`,
            );
            state.mailboxDeleted$.next(del.payload.id);
          }
        } else if (e?.method === VMAILBOX_SOCKET_CONSTANTS.LISTEN_MESSAGE_DELETE) {
          const del = e as VoiceMailboxMessageStateDeleteEvent;
          if (subscribedId !== null && del.payload.mailboxId === subscribedId) {
            console.log(
              `[VMailboxWidgetService] Message deleted for widget ${widgetId} — messageId=${del.payload.id} mailboxId=${del.payload.mailboxId}`,
            );
            state.messageDeleted$.next({
              messageId: del.payload.id,
              mailboxId: del.payload.mailboxId,
            });
          }
        }
      });
  }

  /**
   * Returns the number of widgets in widgetStateMap that are currently subscribed to the
   * given mailbox ID. Used to decide whether a server subscribe/unsubscribe is needed.
   */
  private countMailboxSubscribers(mailboxId: number): number {
    let count = 0;
    for (const s of this.widgetStateMap.values()) {
      if (s.subscribedMailboxId === mailboxId) count++;
    }
    return count;
  }

  private getOrCreateState(widgetId: string): WidgetState {
    if (!this.widgetStateMap.has(widgetId)) {
      this.widgetStateMap.set(widgetId, {
        destroy$: new Subject<void>(),
        mailboxNr$: new BehaviorSubject<string | undefined>(undefined),
        messageCount$: new Subject<VoiceMailboxStateUpdateItem>(),
        mailboxDeleted$: new Subject<number>(),
        messageDeleted$: new Subject<{ messageId: number; mailboxId: number }>(),
        mailboxNameChanged$: new Subject<string>(),
        subscribedMailboxId: null,
        subscribedMailboxNr: null,
        lastKnownItem: null,
        lastKnownName: null,
        audioBlobCache: new Map<string, string>(),
      });
    }
    return this.widgetStateMap.get(widgetId)!;
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  private invalidateAnnouncementCache(widgetId: string, mailboxNr: string, type: AnnouncementType): void {
    const state = this.widgetStateMap.get(widgetId);
    if (!state) return;
    const key = `ann-${mailboxNr}-${type}`;
    const cached = state.audioBlobCache.get(key);
    if (cached) {
      URL.revokeObjectURL(cached);
      state.audioBlobCache.delete(key);
    }
  }
}
